import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  BoxSelect,
  Check,
  Crosshair,
  Globe2,
  LocateFixed,
  Map as MapIcon,
  MapPin,
  RotateCcw,
  Route,
  Square,
  Trash2,
  X,
} from "lucide-react";
import { type KeyboardEvent, type ReactNode, type Ref, useEffect, useId, useMemo, useRef, useState } from "react";

import { ApiError, api, STORAGE_FULL } from "../../api/client";
import type { Deployment, Exercise, LonLat, NamedPlace, Zone } from "../../api/types";
import { attentionSelector, designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { useMediaQuery } from "../../components/useMediaQuery";
import { ProgressBar } from "../../components/Gauge";
import { Spinner } from "../../components/EmptyState";
import { PanelRow, PropRow } from "../../components/panel";
import { Motion, Presence } from "../../components/presence";
import { Select } from "../../components/Select";
import { Badge, Button, Field, IconButton, inputClass, SearchField, SectionLabel } from "../../components/ui";
import { config } from "../../config";
import { formatDateTime } from "../../licence/licence";
import { StorageCallout } from "../../licence/StorageGauge";
import { lang, t, tServer } from "../../i18n/i18n";
import { formatAreaKm2, formatLengthKm } from "../../map/zones/format";
import { extensionPreview, isZoneLevel, preparedFootprint } from "../../map/zones/footprint";
import {
  axisDistanceKm,
  GROUP_LABELS,
  groupResults,
  PLACE_FAR_KM,
  type PlaceResult,
  placeCenter,
  PlaceSearcher,
  placeZoom,
  placeZoomFor,
  type SearchState,
} from "../../map/zones/placeSearch";
import { storeZone, useCreateZone, useRecreateZone, useZoneActions, ZONE_TARGETS } from "../../map/zones/useZones";
import { designateZoneWait } from "../../map/zones/zoneAttention";
import { CLIMATES } from "../../map/zones/climates";
import { useZoneDraw } from "../../map/zones/zoneDraw";
import { useFreeTerrainOffer, useZonesOpen } from "../../map/zones/zonesOpen";
import {
  activeJob,
  durationHint,
  type JobWatch,
  formatPct,
  progressPct,
  roundedDuration,
  slowCause,
  slowness,
  type StepState,
  stepStates,
  watchJob,
  ZONE_STEPS,
  zoneBusy,
  zoneSuperseded,
  zoneWait,
} from "../../map/zones/zoneState";
import { type DeploymentParams, useUi } from "../../state/ui";
import { runInDossierScope } from "../../map/dossierWrites";
import { refreshOrders } from "../../map/orderSync";
import { refreshWeatherViews } from "./TerrainWeather";
import { EMPTY_PARAMETERS, rebaseStoredDraft, withPlace } from "../missionDraft";
import { showMissionField } from "../openMissionData";
import { ZoneDeleteDialog } from "./ZoneDeleteDialog";
import { accountsItem, itemEditable, itemOwned } from "../../sharing/access";
import { InUseDialog, inUseOf } from "../../sharing/InUseDialog";
import { VisibilityControl } from "../../sharing/VisibilityControl";
import { Waiting } from "../../components/Waiting";

/** Étape « Zone » de la vue Manœuvre pour une zone libre (docs/14 §2.2, §2.7) :
 *  - `FreeZoneBuilder` : recherche de lieu (géocodeur Mapbox, MGRS, coordonnées), « Tracer l'axe » (deux clics sur
 *    la carte, indépendants du camp affiché), carte « Créer la zone » (nom, surface préparée, « Préparer le
 *    terrain ») ;
 *  - `ZoneCard` : nom modifiable, climat, état durable, étapes 1 à 6 de la préparation (barre, estimation, rang
 *    dans la file), « Annuler », « Réessayer » (compte à rebours d'un 429), « Étendre la zone » (emprise tracée sur
 *    la carte), « Supprimer » (dossiers qui la citent, suppression forcée) ;
 *  - `RecreateZoneCallout` : zone gardée dans le dossier par un import ou une réception refusés par la file. */

/** Libellé du climat proposé par défaut : celui que rend le serveur (`climate_label`, en français), traduit. */
const climateLabel = (zone: Zone) =>
  zone.climate_label ? tServer(zone.climate_label) : t(CLIMATES.find((c) => c.key === zone.climate)?.label ?? zone.climate);

/** Durée lisible : « 6 min », « 45 s ». */
function durationText(ms: number): string {
  const { value, unit } = roundedDuration(ms);
  return unit === "min" ? t("{n} min", { n: value }) : t("{n} s", { n: value });
}

/** Instant présent, relu chaque seconde tant que `active` (compte à rebours, estimation). */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

/** Compte à rebours d'un refus 429 (`Retry-After`) pour `key` (une zone, ou `create`) : millisecondes restantes. */
function useRetryCountdown(key: string): number {
  const until = useZoneDraw((s) => s.retryUntil[key]);
  const now = useNow(Boolean(until));
  const left = until ? until - now : 0;
  useEffect(() => {
    if (until && left <= 0) useZoneDraw.getState().blockRetry(key, null);
  }, [until, left, key]);
  return Math.max(0, left);
}

/** Contexte du dossier ouvert pour le tracé : dossier enregistré, niveau, clé de gabarit (largeur doctrinale du
 *  fuseau prévu) et mission de l'ordre. */
export function useAxisContext() {
  const exerciseId = useUi((s) => s.exerciseId);
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises.find((e) => e.id === exerciseId) ?? null;
  const level = exercise?.parameters.level ?? null;
  // Le sélecteur ajoute les références du dossier ouvert (`base=`, mode comptes) : sa clé porte le dossier, comme dans
  // les Données initiales, pour ne pas servir les entrées « du dossier » d'un autre dossier.
  const base = exercise && accountsItem(exercise) ? exercise.id : null;
  const { data: forces = [] } = useQuery({
    queryKey: ["forces", level, base],
    queryFn: () => api.forces(level!, base),
    enabled: Boolean(level) && level !== "CENTAC",
  });
  const levelKey = forces.find((f) => f.ref === exercise?.parameters.odb)?.level_key ?? null;
  return { exercise, level, levelKey, mission: exercise?.parameters.mission ?? null };
}

// --- Recherche de lieu ---------------------------------------------------------------------------------------------

/** `initialText` : le lieu nommé dans les Données initiales, montré d'office (la carte y est déjà centrée). */
function PlaceSearch({ inputRef, initialText = "" }: { inputRef?: Ref<HTMLInputElement>; initialText?: string }) {
  const flyTo = useUi((s) => s.requestFlyTo);
  const setPlaceName = useZoneDraw((s) => s.setPlaceName);
  const [text, setText] = useState(initialText);
  const [state, setState] = useState<SearchState>({ status: "idle" });
  const [active, setActive] = useState(0);
  const listId = useId();
  const searcher = useMemo(
    () => new PlaceSearcher({ token: config.mapboxToken, language: lang, onState: setState }),
    [],
  );
  useEffect(() => () => searcher.cancel(), [searcher]);
  const results = state.status === "done" ? state.results : [];
  const groups = groupResults(results);
  const flat = groups.flatMap((g) => g.items);

  const choose = (result: PlaceResult) => {
    flyTo(placeCenter(result), placeZoom(result));
    // Le nom du lieu préremplit celui de la zone ; ses coordonnées ne sont jamais enregistrées.
    if (result.group !== "coords") setPlaceName(result.name);
    setText(result.group === "coords" ? text : result.name);
    close();
  };
  const close = () => {
    searcher.cancel();
    setState({ status: "idle" });
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!flat.length) return;
    if (e.key === "Escape") {
      // Échap ferme d'abord la liste (le texte reste) ; un second Échap efface le champ.
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a + (e.key === "ArrowDown" ? 1 : flat.length - 1)) % flat.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const result = flat[Math.min(active, flat.length - 1)];
      if (result) choose(result);
    }
  };

  let index = -1;
  return (
    <div className="space-y-1.5">
      <div data-attention={ZONE_TARGETS.search} className="rounded-lg">
        <SearchField
          value={text}
          onChange={(value) => {
            setText(value);
            setActive(0);
            searcher.input(value);
          }}
          onClear={() => {
            setText("");
            searcher.input("");
          }}
          onKeyDown={onKeyDown}
          combobox={{ listId, expanded: flat.length > 0, activeId: flat.length ? `${listId}-${Math.min(active, flat.length - 1)}` : undefined }}
          inputRef={inputRef}
          placeholder={t("Lieu, coordonnées ou MGRS")}
          ariaLabel={t("Rechercher un lieu, des coordonnées ou un MGRS")}
        />
      </div>
      <Presence show={state.status === "loading"}>
        <Motion origin="fade" className="flex min-h-8 items-center gap-2 px-1 text-meta text-ink-muted">
          <Spinner decorative /> {<Waiting>{t("Recherche…")}</Waiting>}
        </Motion>
      </Presence>
      <Presence show={state.status === "unavailable"}>
        <Callout tone="warn">{t("Recherche indisponible : saisissez des coordonnées ou un MGRS")}</Callout>
      </Presence>
      <Presence show={state.status === "short"}>
        <Motion origin="fade" className="px-1 text-meta text-ink-muted">
          {t("Trois caractères au moins.")}
        </Motion>
      </Presence>
      <Presence show={state.status === "done" && results.length === 0}>
        <Motion origin="fade" className="px-1 text-meta text-ink-muted">
          {t("Aucun lieu trouvé.")}
        </Motion>
      </Presence>
      <Presence show={flat.length > 0}>
        <Motion origin="fade" role="listbox" id={listId} aria-label={t("Lieux trouvés")} className="space-y-1">
          {groups.map((group) => (
            <div key={group.group} role="group" aria-label={t(GROUP_LABELS[group.group])}>
              <SectionLabel inset className="px-2 pt-1 pb-0.5">
                {t(GROUP_LABELS[group.group])}
              </SectionLabel>
              {group.items.map((result) => {
                index += 1;
                const i = index;
                return (
                  <PanelRow
                    key={result.id}
                    data-place-row={String(i)}
                    icon={result.group === "coords" ? <LocateFixed size={14} /> : <MapPin size={14} />}
                    title={result.name}
                    detail={result.detail || undefined}
                    option={{ id: `${listId}-${i}`, active: i === active }}
                    onClick={() => choose(result)}
                    clickTitle={t("Centrer la carte sur {place}", { place: result.name })}
                  />
                );
              })}
            </div>
          ))}
        </Motion>
      </Presence>
    </div>
  );
}

// --- Création de la zone -------------------------------------------------------------------------------------------

/** Ligne du camp du dossier à l'étape Terrain de la vue Manœuvre, avec « Terrain libre… » pour en préparer un à la
 *  place — seulement quand la règle d'offre partagée avec les Données initiales le permet (`freeTerrainOffered` :
 *  serveur ouvert, carte mondiale, hors CENTAC) et que le bloc de création n'est pas déjà ouvert. */
export function CampRow({
  name,
  centac,
  builderShown,
  onFreeTerrain,
}: {
  name: string;
  centac: boolean;
  builderShown: boolean;
  onFreeTerrain: () => void;
}) {
  const { offered } = useFreeTerrainOffer(centac);
  return (
    <PanelRow
      icon={<MapIcon size={14} />}
      title={name}
      detail={t("Camp")}
      actions={
        offered && !builderShown ? (
          <Button variant="ghost" size="sm" onClick={onFreeTerrain}>
            {t("Terrain libre…")}
          </Button>
        ) : undefined
      }
    />
  );
}

/** Ligne grise du lieu nommé : « Lieu nommé : Tartu, Tartumaa, Estonie », avec « Recentrer ». */
function NamedPlaceRow({ place, onRecenter }: { place: NamedPlace; onRecenter: () => void }) {
  const where = place.region ? `${place.name}, ${place.region}` : place.name;
  return (
    <div className="flex min-h-8 flex-wrap items-center gap-x-2 gap-y-0.5 px-1 py-1.5 text-meta leading-snug text-ink-muted">
      <span className="min-w-0 break-words">{t("Lieu nommé : {lieu}", { lieu: where })}</span>
      <Button variant="link" onClick={onRecenter}>
        {t("Recentrer")}
      </Button>
    </div>
  );
}

/** Bloc « Terrain libre » : recherche, tracé de l'axe, confirmation. `onClose` : retour à la carte actuelle du
 *  dossier (`keep` : un camp, ou le terrain libre que le nouveau remplacera), aussi appelé à la création.
 *  Lieu nommé dans les Données initiales (`parameters.place`) : la carte s'y centre à chaque ouverture de la vue
 *  Manœuvre (`map/placeFraming.ts`, décidé par la carte, pas par ce bloc), son nom préremplit la recherche et le nom
 *  du terrain, « Recentrer » y ramène ;
 *  « Tracer l'axe » est l'action attendue. Un axe tracé loin de lui (plus de 150 km) est signalé : la météo choisie
 *  sera revue pour le terrain placé. « Garder le camp » (ou « ce terrain ») retire le lieu du dossier. */
export function FreeZoneBuilder({ onClose, keep = "camp" }: { onClose?: () => void; keep?: "camp" | "terrain" }) {
  const { exercise, level, levelKey, mission } = useAxisContext();
  const exerciseId = useUi((s) => s.exerciseId);
  const requestSave = useUi((s) => s.requestSave);
  const notify = useUi((s) => s.notify);
  const drawing = useZoneDraw((s) => s.drawing);
  const axis = useZoneDraw((s) => s.axis);
  const startAxis = useZoneDraw((s) => s.startAxis);
  const cancelDrawing = useZoneDraw((s) => s.cancelDrawing);
  const clearPending = useZoneDraw((s) => s.clearPending);
  const placeName = useZoneDraw((s) => s.placeName);
  // Parcours guidé (dossier enregistré en « Terrain libre ») : la recherche de lieu prend le curseur à l'ouverture
  // du bloc (au pointeur fin seulement) et « Tracer l'axe » pulse comme l'action attendue, jusqu'au début du tracé.
  const guided = useZoneDraw((s) => s.guided);
  const endGuide = useZoneDraw((s) => s.endGuide);
  const setTerrainFor = useZoneDraw((s) => s.setTerrainFor);
  const searchRef = useRef<HTMLInputElement>(null);
  // Au doigt (iPad), pas de mise au point d'office : le clavier virtuel couvrirait la carte ; « Tracer l'axe » pulse.
  const coarse = useMediaQuery("(pointer: coarse)");
  const create = useCreateZone();
  const wait = useRetryCountdown("create");
  const [name, setName] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const flyTo = useUi((s) => s.requestFlyTo);
  const setPlaceName = useZoneDraw((s) => s.setPlaceName);
  // Lieu nommé du dossier (jamais en CENTAC) : attendu tant que le terrain n'est pas placé.
  const named = exercise && exercise.parameters.level !== "CENTAC" ? (exercise.parameters.place ?? null) : null;
  const recenter = () => named && flyTo([named.lon, named.lat], placeZoomFor(level));
  // Centrage à l'ouverture : la carte en décide (`usePlaceFraming`), une fois chargée, après ses cadrages automatiques.
  useEffect(() => {
    if (!named || !exercise || axis || config.mapEngine !== "mapbox") return;
    setPlaceName(named.name);
  }, [named?.lon, named?.lat, named?.name, exercise?.id]);

  useEffect(() => {
    // Lieu nommé : la vue y est déjà, l'action attendue est le tracé de l'axe (pas la recherche).
    if (!guided || coarse || named) return;
    // Après l'entrée du panneau, pour que le curseur ne fasse pas défiler une vue encore en mouvement.
    const frame = window.requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(frame);
  }, [guided, coarse]);

  // Axe accepté : le nom proposé est celui du dernier lieu choisi, sinon celui du dossier.
  useEffect(() => {
    if (axis) setName((current) => current || placeName || exercise?.name || "");
  }, [axis, placeName, exercise?.name]);

  if (config.mapEngine !== "mapbox") {
    return (
      <Callout tone="info" icon={Globe2} title={t("Le terrain libre demande la carte mondiale")} className="mx-2 my-1">
        {t("Le terrain libre se trace sur la carte Mapbox, absente de ce serveur : choisissez un camp dans les Données initiales.")}
      </Callout>
    );
  }

  const drawingAxis = drawing?.mode === "axis";
  const trace = () => {
    if (!exerciseId || !exercise) {
      notify({ text: t("Enregistrez le dossier pour préparer un terrain libre."), tone: "info" });
      requestSave();
      return;
    }
    if (!level || !isZoneLevel(level)) return showMissionField("dossier-level");
    if (!mission) return showMissionField("dossier-mission");
    // Lieu nommé dans les Données initiales : la carte le rejoint, au zoom du niveau, avant le premier point (demande
    // d'Alexis du 02/11/2026 : « la carte doit se centrer et zoomer suffisamment sur la localité »).
    recenter();
    startAxis({ level, levelKey, mission });
  };
  const blockedReason = !exercise ? "save" : !level || !isZoneLevel(level) ? "level" : !mission ? "mission" : null;

  const result = axis ? preparedFootprint(axis.start, axis.end, axis.level, axis.levelKey) : null;
  const footprint = result?.ok ? result.footprint : null;
  const error = create.error instanceof ApiError ? create.error : create.error ? new ApiError(0, t("Création impossible : réessayez.")) : null;
  const hasName = Boolean(name.trim());
  // Axe tracé loin du lieu nommé : la météo choisie sur son climat sera revue pour le terrain placé (serveur).
  const farKm = axis && named ? Math.round(axisDistanceKm(axis, named)) : 0;
  const submit = () => {
    if (!axis || !exercise || !footprint) return;
    if (!hasName) {
      designate(nameRef.current, "field");
      notify({ text: t("Nommez le terrain pour le préparer."), tone: "info" });
      return;
    }
    create.mutate(
      { name: name.trim(), axis: { start: axis.start, end: axis.end }, exercise },
      {
        onSuccess: () => {
          setName("");
          setTerrainFor(null); // choix « Terrain libre » des Données initiales abouti
          onClose?.();
        },
      },
    );
  };

  /** « Garder le camp » / « Garder ce terrain » : le dossier n'attend plus de nouveau terrain — son lieu nommé est
   *  retiré (écriture du dossier, dans sa portée). */
  const keepCurrent = () => {
    endGuide();
    setTerrainFor(null); // la carte actuelle du dossier reste la sienne
    onClose?.();
    if (!named || !exercise) return;
    const id = exercise.id;
    void runInDossierScope(queryClient, id, async () => {
      const latest = queryClient.getQueryData<Exercise[]>(["exercises"])?.find((e) => e.id === id) ?? exercise;
      const ex = await api.updateExercise(latest, { parameters: withPlace({ ...EMPTY_PARAMETERS, ...latest.parameters }, null) });
      // Dans la portée : l'écriture suivante (de la carte) part de la nouvelle version.
      queryClient.setQueryData<Exercise[]>(["exercises"], (list) => list?.map((e) => (e.id === ex.id ? { ...e, ...ex } : e)));
      queryClient.setQueriesData<Deployment>(
        { predicate: (q) => q.queryKey[0] === "deployment" && (q.queryKey[1] as DeploymentParams | null)?.exercise_id === ex.id },
        (d) => (d && d.exercise_version != null ? { ...d, exercise_version: ex.version } : d),
      );
      return ex;
    })
      .then((ex) => {
        void queryClient.invalidateQueries({ queryKey: ["exercises"] });
        // La météo suit de nouveau la carte du dossier : les vues qui la lisent la relisent, les ordres recomposés aussi.
        if (ex.refreshed) refreshOrders(queryClient, ex.id);
        refreshWeatherViews(queryClient, ex.id);
        // Brouillon des Données initiales gardé sur ce poste : le lieu y est retiré aussi, le reste est gardé.
        rebaseStoredDraft(ex, ["parameters.place"]);
      })
      .catch((error: unknown) =>
        notify({
          text: t("Lieu du nouveau terrain non retiré"),
          detail: error instanceof ApiError ? tServer(error.message) : t("Serveur injoignable"),
          tone: "error",
        }),
      );
  };

  return (
    <div className="space-y-2.5 px-2 py-1.5">
      <PlaceSearch inputRef={searchRef} initialText={named?.name ?? ""} />
      {named && <NamedPlaceRow place={named} onRecenter={recenter} />}
      <Presence show={!axis}>
        <Motion origin="fade" className="flex flex-wrap items-center gap-2 py-1">
          {drawingAxis ? (
            <Button variant="secondary" onClick={cancelDrawing}>
              <X size={14} /> {t("Annuler le tracé")}
            </Button>
          ) : (
            <Button
              data-attention={ZONE_TARGETS.trace}
              blocked={blockedReason !== null}
              highlight={guided || Boolean(named)}
              onBlocked={trace}
              onClick={trace}
            >
              <Route size={14} /> {t("Tracer l'axe")}
            </Button>
          )}
          {onClose && !drawingAxis && (
            <Button variant="ghost" onClick={keepCurrent}>
              {keep === "terrain" ? t("Garder ce terrain") : t("Garder le camp")}
            </Button>
          )}
        </Motion>
      </Presence>
      <Presence show={drawingAxis}>
        <Motion origin="fade" className="px-1 text-meta leading-snug text-ink-muted">
          {drawing?.points.length
            ? t("Cliquez sur la carte l'extrémité côté ennemi. Le terrain préparé entoure le fuseau de quelques kilomètres.")
            : t("Cliquez sur la carte l'arrière du fuseau, puis son extrémité côté ennemi.")}
        </Motion>
      </Presence>
      <Presence show={Boolean(axis && footprint)}>
        {axis && footprint && (
          <Motion origin="top" data-attention="zone-confirm" className="rounded-xl border border-line bg-surface p-3 shadow-card">
            <h4 className="mb-2 text-title font-semibold text-ink">{t("Créer le terrain")}</h4>
            <Field label={t("Nom du terrain")}>
              <input
                ref={nameRef}
                className={inputClass}
                value={name}
                maxLength={80}
                placeholder={t("ex. Tapa")}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                }}
              />
            </Field>
            <div className="mt-2">
              <PropRow label={t("Longueur de l'axe")}>{formatLengthKm(footprint.length_m)}</PropRow>
              <PropRow label={t("Surface préparée")}>{formatAreaKm2(footprint.area_km2)}</PropRow>
            </div>
            <p className="mt-1.5 text-meta leading-snug text-ink-muted">
              {t("Generex rassemble routes, cours d'eau, bois, localités et relief du terrain. La préparation se poursuit si vous changez de vue.")}
            </p>
            <Presence show={farKm > PLACE_FAR_KM}>
              {named && farKm > PLACE_FAR_KM && (
                <Motion origin="fade">
                  <p className="mt-1.5 text-meta leading-snug text-warn">
                    {t("Ce terrain est à {n} km de « {lieu} » : la météo choisie sera revue pour lui.", { n: farKm, lieu: named.name })}
                  </p>
                </Motion>
              )}
            </Presence>
            <Presence show={Boolean(error)}>
              {error && (
                <div className="mt-2">
                  {error.code === STORAGE_FULL ? <StorageCallout message={error.message} /> : <Callout tone="danger">{error.message}</Callout>}
                </div>
              )}
            </Presence>
            <div className="mt-2.5 flex flex-wrap items-center justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  clearPending();
                  create.reset();
                }}
              >
                {t("Annuler")}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  create.reset();
                  trace();
                }}
              >
                <RotateCcw size={14} /> {t("Retracer")}
              </Button>
              <Button
                onClick={submit}
                disabled={create.isPending}
                blocked={wait > 0}
                onBlocked={() => notify({ text: t("Préparation de terrain demandée récemment : réessayez dans {duree}.", { duree: durationText(wait) }), tone: "info" })}
              >
                <Check size={14} />{" "}
                {wait > 0
                  ? t("Préparer dans {duree}", { duree: durationText(wait) })
                  : create.isPending
                    ? <Waiting>{t("Mise en file…")}</Waiting>
                    : t("Préparer le terrain")}
              </Button>
            </div>
          </Motion>
        )}
      </Presence>
    </div>
  );
}

// --- Préparation : étapes et barre ---------------------------------------------------------------------------------

function StepIcon({ state }: { state: StepState }) {
  if (state === "done") return <Check size={14} className="text-ok" aria-label={t("Étape faite")} />;
  if (state === "active") return <Spinner label={t("Étape en cours")} />;
  return <span role="img" aria-label={t("Étape à faire")} className="size-3 rounded-full border-[1.5px] border-line-strong" />;
}

/** Observations des tâches par ce poste (`JobWatch`), par zone : gardées hors du composant pour qu'un changement de vue
 *  (la carte se remonte) ne fasse pas oublier depuis quand le pourcentage n'a pas bougé. */
const watches = new Map<string, JobWatch>();

/** Observation de la tâche en cours de la zone, relevée à chaque rendu (`watchJob` rend le même objet tant que rien ne
 *  change : relevé idempotent). null en file ou sans tâche active. */
function jobWatch(zone: Zone, now: number): JobWatch | null {
  const job = activeJob(zone);
  if (!job || job.status !== "preparing") {
    watches.delete(zone.id);
    return null;
  }
  const next = watchJob(watches.get(zone.id), job.key, progressPct(job.progress, "preparing"), now, zone.elapsed_s, zone.idle_s);
  watches.set(zone.id, next);
  return next;
}

/** Carte de progression : six étapes à coche, barre, durée (habituelle dès la première seconde, puis temps restant)
 *  ou rang dans la file et durée habituelle, avis calme quand les serveurs de données sont lents, « Annuler ». Cible
 *  désignée quand une action attend la fin de la préparation. Les durées viennent du serveur (`expected_s`,
 *  `elapsed_s`, `idle_s`) ; aucun horodatage du serveur n'est comparé à l'horloge du poste (`JobWatch`). */
export function ZoneProgressCard({
  title,
  zone,
  onCancel,
  cancelling = false,
  target = ZONE_TARGETS.progress,
}: {
  title: string;
  zone: Zone;
  /** « Annuler la préparation » (absent : la carte ne fait que montrer l'avancement, vérification d'un OPO). */
  onCancel?: () => void;
  cancelling?: boolean;
  /** Cible `data-attention` de la carte. */
  target?: string;
}) {
  const job = activeJob(zone);
  const status = job?.status ?? zone.status;
  const progress = job?.progress ?? null;
  const position = zone.queue_position;
  const states = stepStates(progress, status);
  const pct = progressPct(progress, status);
  // Relu chaque seconde pendant la préparation ; l'instant lui-même est pris au rendu (le premier relevé d'une tâche
  // ne doit pas partir d'un instant figé depuis le montage de la carte).
  useNow(status === "preparing");
  const now = Date.now();
  const watch = jobWatch(zone, now);
  const hint = watch ? durationHint(watch, pct, zone.expected_s, now, zone.osm_source) : null;
  const slow = watch ? slowness(watch, now) : null;
  const stopMinutes = zone.level === "CORPS" ? 30 : 15;
  const usual = zone.expected_s ? t("Durée habituelle pour ce terrain : environ {duree}.", { duree: durationText(zone.expected_s * 1000) }) : null;
  const queued =
    position && position > 1
      ? position === 2
        ? t("En file : une préparation avant celle-ci.")
        : t("En file : {n} préparations avant celle-ci.", { n: position - 1 })
      : t("En file : la préparation commence dans un instant.");
  return (
    <div data-attention={target} className="rounded-xl border border-line bg-surface p-3 shadow-card">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h4 className="text-title font-semibold text-ink">{title}</h4>
        <span className="text-meta text-ink-muted tabular-nums">{t("{pct} %", { pct: formatPct(pct) })}</span>
      </div>
      <ProgressBar value={pct / 100} tone="run" label={t("Préparation du terrain : {pct} %", { pct: formatPct(pct) })} />
      <p className="mt-1.5 text-meta leading-snug text-ink-muted" aria-live="polite">
        {status === "queued"
          ? usual
            ? `${queued} ${usual}`
            : queued
          : hint?.kind === "remaining"
            ? t("Environ {duree} restantes.", { duree: durationText(hint.ms) })
            : hint?.kind === "usual" && usual
              ? usual
              : <Waiting>{t("Estimation de la durée en cours…")}</Waiting>}
      </p>
      {/* Pourcentage immobile (vu par ce poste) : avis calme, jamais d'alerte pour une attente normale, qui n'accuse les
          serveurs de données que pendant les étapes qui les attendent ; après 10 minutes, le rappel de l'arrêt
          automatique du serveur au bout de 15 minutes sans progrès. */}
      {/* Veilleur de blocage du serveur : 30 minutes pour un terrain de corps, 15 aux autres niveaux. */}
      <Presence show={slow !== null}>
        {slow !== null && (
          <Callout tone="info" className="mt-2">
            {slowCause(progress, zone.osm_source) === "network"
              ? slow === "stalled"
                ? t("Les serveurs de données tardent à répondre. Sans aucun progrès pendant {minutes} minutes, la préparation s'arrêtera d'elle-même ; vous pourrez alors la relancer.", { minutes: stopMinutes })
                : t("Les serveurs de données répondent lentement : la préparation continue sur le serveur, même si vous quittez cette vue.")
              : slow === "stalled"
                ? t("Le calcul du terrain prend plus de temps que d'habitude. Sans aucun progrès pendant {minutes} minutes, la préparation s'arrêtera d'elle-même ; vous pourrez alors la relancer.", { minutes: stopMinutes })
                : t("Le calcul du terrain prend un moment : la préparation continue sur le serveur, même si vous quittez cette vue.")}
          </Callout>
        )}
      </Presence>
      <ul className="mt-1.5">
        {ZONE_STEPS.map((step, i) => (
          <li key={step.key} className="flex min-h-8 items-center gap-2.5 py-1.5 text-ui">
            <span className="flex size-5 shrink-0 items-center justify-center">
              <Motion key={states[i]} origin="zoom" className="flex items-center justify-center">
                <StepIcon state={states[i]!} />
              </Motion>
            </span>
            <span className={clsx("min-w-0 break-words", states[i] === "todo" ? "text-ink-muted" : "text-ink")}>{t(step.label)}</span>
          </li>
        ))}
      </ul>
      {onCancel && (
        <div className="pt-2.5">
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={cancelling}>
            <Square size={14} /> {cancelling ? <Waiting>{t("Annulation…")}</Waiting> : t("Annuler la préparation")}
          </Button>
        </div>
      )}
    </div>
  );
}

// --- Fiche de zone -------------------------------------------------------------------------------------------------

/** Explication des droits du compte sur le terrain (comptes nominatifs), désignée par une commande hors droits. */
const ZONE_RIGHTS_TARGET = "zone-rights";

/** État durable de la zone, en pastille (R6 : seulement l'exception ; une zone prête n'en a pas). */
function ZoneStatusBadge({ zone }: { zone: Zone }) {
  const pending = zone.status === "ready" ? zone.pending_status : null;
  const status = pending ?? zone.status;
  if (status === "ready" || (pending === "cancelled" && zone.status === "ready")) return null;
  const pct = progressPct(pending ? zone.pending_progress : zone.progress, status);
  const badge =
    status === "queued"
      ? { tone: "run" as const, text: pending ? t("Extension en file") : t("En file") }
      : status === "preparing"
        ? { tone: "run" as const, text: t("{pct} %", { pct: formatPct(pct) }) }
        : status === "failed"
          ? { tone: "danger" as const, text: pending ? t("Extension en échec") : t("Échec") }
          : { tone: "neutral" as const, text: zoneSuperseded(zone) ? t("Interrompue") : t("Annulé") };
  return (
    <Motion key={`${status}-${pending ?? ""}`} origin="fade" className="inline-flex">
      <Badge dot tone={badge.tone}>
        {badge.text}
      </Badge>
    </Motion>
  );
}

/** Nom de la zone, modifiable en place (Entrée ou sortie du champ enregistrent, Échap rétablit). */
function ZoneName({ zone, editable = true, onBlocked }: { zone: Zone; editable?: boolean; onBlocked?: () => void }) {
  const { rename } = useZoneActions(zone);
  const [value, setValue] = useState(zone.name);
  useEffect(() => setValue(zone.name), [zone.name]);
  const commit = () => {
    const next = value.trim();
    if (!next) return setValue(zone.name);
    if (next !== zone.name) rename.mutate(next);
  };
  return (
    <Field label={t("Nom du terrain")}>
      <input
        className={inputClass}
        value={value}
        maxLength={80}
        // Terrain d'un autre compte (docs/17 §3.6) : le nom se lit ; l'appui désigne qui peut le modifier.
        readOnly={!editable}
        onClick={editable ? undefined : onBlocked}
        onChange={(e) => setValue(e.target.value)}
        onBlur={editable ? commit : undefined}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") {
            e.stopPropagation();
            setValue(zone.name);
          }
        }}
      />
    </Field>
  );
}

/** Climats proposés au réglage « Climat » : ceux que le serveur juge plausibles au lieu (`climates_allowed`, tous à
 *  défaut), dans l'ordre des climats, plus le climat choisi s'il n'en fait pas partie (gardé, et signalé). */
export function climateChoices(zone: Pick<Zone, "climate" | "climate_overridden" | "climates_allowed">): { key: string; label: string; unusual: boolean }[] {
  const allowed = zone.climates_allowed;
  return CLIMATES.filter((c) => !allowed || allowed.includes(c.key) || (zone.climate_overridden && c.key === zone.climate)).map((c) => ({
    ...c,
    unusual: Boolean(allowed) && !allowed!.includes(c.key),
  }));
}

/** `highlightRemove` : le dispositif refuse la zone (prête mais sans terre exploitable) — « Supprimer » est l'action
 *  attendue. `weather` : réglage « Météo de l'exercice » du dossier ouvert, sous le climat (étape Terrain). */
export function ZoneCard({ zone, highlightRemove = false, weather }: { zone: Zone; highlightRemove?: boolean; weather?: ReactNode }) {
  const actions = useZoneActions(zone);
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const flyTo = useUi((s) => s.requestFlyTo);
  const drawing = useZoneDraw((s) => s.drawing);
  const extension = useZoneDraw((s) => s.extension);
  const startExtend = useZoneDraw((s) => s.startExtend);
  const cancelDrawing = useZoneDraw((s) => s.cancelDrawing);
  const clearPending = useZoneDraw((s) => s.clearPending);
  const wait = useRetryCountdown(zone.id);
  // Zone libre fermée sur ce serveur : la zone reste servie et se gère, mais rien ne se met en file (relance,
  // extension) ; ces boutons l'expliquent au lieu d'envoyer une demande refusée.
  const open = useZonesOpen();
  const [deleting, setDeleting] = useState(false);
  const ready = zone.status === "ready";
  const busy = zoneBusy(zone);
  const extending = ready && zone.pending_status !== null && zone.pending_status !== "cancelled";
  const failed = zone.status === "failed" || zone.pending_status === "failed";
  const cancelled = zone.status === "cancelled";
  // Interrompue parce qu'un autre terrain de la licence est passé en priorité (création ou extension) : « Reprendre la
  // préparation » est l'action attendue.
  const superseded = zoneSuperseded(zone) && !busy;
  const ring = zone.footprint?.coordinates[0] as LonLat[] | undefined;
  const pendingExtension = extension?.zoneId === zone.id ? extension : null;
  const preview = pendingExtension && ring ? extensionPreview(ring, pendingExtension.bbox, zone.level) : null;
  const drawingExtension = drawing?.mode === "extend" && drawing.zoneId === zone.id;
  // Refus par ricochet (docs/17 §3.13) : fenêtre dédiée, pas l'encadré d'erreur.
  const failures = [actions.retry.error, actions.extend.error, actions.cancel.error, actions.climate.error, actions.rename.error];
  const inUse = failures.map(inUseOf).find(Boolean) ?? null;
  const actionError = failures.find((e) => e instanceof ApiError && e.status !== 429 && !inUseOf(e)) as ApiError | undefined;
  const closeInUse = () => [actions.retry, actions.extend, actions.cancel, actions.climate, actions.rename].forEach((m) => m.reset());
  // Droits du compte sur le terrain (docs/17 §3.6) : modifier (renommer, climat, étendre, relancer, annuler) et
  // supprimer ; une commande hors droits désigne l'explication, qui nomme le propriétaire. Tout en mode hérité.
  const editable = itemEditable(zone);
  const owned = itemOwned(zone);
  const designateRights = () => designate(attentionSelector(ZONE_RIGHTS_TARGET));
  const climates = climateChoices(zone);
  const unusualClimate = zone.climate_overridden && climates.some((c) => c.key === zone.climate && c.unusual);

  const expected = zoneWait(zone);
  const extend = () => {
    if (!editable) return designateRights();
    if (!open) return notifyZonesClosed(notify, true);
    if (!ready || busy) return designateZoneWait(zone, "extend");
    if (!ring) return;
    startExtend(zone.id, zone.level ?? "", ring);
  };
  const retry = () => (editable ? actions.retry.mutate() : designateRights());
  const retryLabel =
    wait > 0
      ? t("Réessayer dans {duree}", { duree: durationText(wait) })
      : actions.retry.isPending
        ? <Waiting>{t("Relance…")}</Waiting>
        : superseded
          ? t("Reprendre la préparation")
          : t("Réessayer");

  return (
    <div className="space-y-2.5 px-2 py-1.5">
      <PanelRow
        data-attention={ZONE_TARGETS.card}
        icon={<MapIcon size={14} />}
        title={zone.name}
        detail={zone.area_km2 !== null ? t("Terrain libre · {area}", { area: formatAreaKm2(zone.area_km2) }) : t("Terrain libre")}
        state={<ZoneStatusBadge zone={zone} />}
        actions={
          zone.bbox ? (
            <IconButton
              size="xs"
              title={t("Cadrer sur le terrain")}
              aria-label={t("Cadrer sur le terrain")}
              onClick={() => flyTo([(zone.bbox![0] + zone.bbox![2]) / 2, (zone.bbox![1] + zone.bbox![3]) / 2], 9)}
            >
              <Crosshair size={14} />
            </IconButton>
          ) : undefined
        }
      />
      <ZoneName zone={zone} editable={editable} onBlocked={designateRights} />
      <Presence show={!owned}>
        {!owned && (
          <Callout tone="info">
            <span data-attention={ZONE_RIGHTS_TARGET} className="block rounded-md">
              {editable
                ? t("Terrain de l'unité : tous les comptes le modifient ; seuls les administrateurs le suppriment ou lui attribuent un propriétaire.")
                : t("Terrain de {nom} : seuls son propriétaire et les administrateurs le modifient ou le suppriment. Vous pouvez l'employer dans vos dossiers.", {
                    nom: zone.owner?.name ?? t("l'unité"),
                  })}
            </span>
          </Callout>
        )}
      </Presence>
      {/* Comptes nominatifs (docs/17 §3.6) : visibilité du terrain, réglée par son propriétaire et les administrateurs. */}
      {accountsItem(zone) && (
        <div className="flex min-h-8 flex-wrap items-center gap-2 py-1.5">
          <VisibilityControl
            item={zone}
            users={zone.exercises}
            onChange={(visibility) => api.zoneVisibility(zone.id, visibility)}
            onSaved={(next) => storeZone(queryClient, next)}
          />
        </div>
      )}
      <div data-attention={ZONE_TARGETS.climate} className="rounded-lg">
        <Field
          label={t("Climat")}
          hint={[
            t("Il règle la météo et les saisons de l'ordre ; l'ordre est recomposé à son ouverture."),
            zone.climates_allowed && t("Seuls les climats possibles à ce lieu sont proposés."),
            // Terrain en préparation, climat déduit du lieu : il peut encore changer (relief, part aride).
            !ready && !zone.climate_overridden && t("Climat provisoire : il sera confirmé à la fin de la préparation du terrain."),
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <Select
            // Choix affiché dès l'appui, le temps de l'enregistrement (la liste ne revient pas à l'ancienne valeur).
            value={actions.climate.isPending ? (actions.climate.variables ?? "auto") : zone.climate_overridden ? zone.climate : "auto"}
            aria-disabled={!editable || undefined}
            onChange={(e) => (editable ? actions.climate.mutate(e.target.value) : designateRights())}
            aria-label={t("Climat du terrain")}
          >
            <option value="auto">
              {zone.climate_overridden ? t("Par défaut, selon le terrain") : t("Par défaut : {climat}", { climat: climateLabel(zone) })}
            </option>
            {climates.map((c) => (
              <option key={c.key} value={c.key}>
                {t(c.label)}
              </option>
            ))}
          </Select>
        </Field>
        <Presence show={actions.climate.isPending}>
          <Motion origin="fade" className="mt-1 flex items-center gap-1.5 text-meta text-ink-muted">
            <Spinner decorative />
            <Waiting>{t("Enregistrement du climat…")}</Waiting>
          </Motion>
        </Presence>
        {/* Climat choisi avant que le lieu ne le juge impossible (dossier ancien) : gardé, signalé, avec le retour au
            climat par défaut. */}
        <Presence show={unusualClimate && !actions.climate.isPending}>
          <Motion origin="fade" className="mt-1.5">
            <Callout
              tone="warn"
              action={
                <Button variant="link" onClick={() => (editable ? actions.climate.mutate("auto") : designateRights())}>
                  {t("Revenir au climat par défaut")}
                </Button>
              }
            >
              {t("Climat inhabituel pour ce lieu : la météo et les saisons de l'ordre en dépendent.")}
            </Callout>
          </Motion>
        </Presence>
      </div>
      {weather}

      {/* Préparation ou extension en file / en cours : étapes, barre, estimation. */}
      <Presence show={busy}>
        {busy && (
          <Motion origin="top">
            <ZoneProgressCard
              title={
                extending
                  ? zone.refreshing
                    ? t("Mise à jour des fichiers du terrain")
                    : t("Extension du terrain")
                  : t("Préparation du terrain")
              }
              zone={zone}
              onCancel={() => (editable ? actions.cancel.mutate() : designateRights())}
              cancelling={actions.cancel.isPending}
            />
          </Motion>
        )}
      </Presence>

      {/* Échec ou annulation : message et action attendue selon la cause (`zoneWait`) — « Réessayer » qui clignote
          (compte à rebours d'un 429) ; faute d'espace, la jauge et « Libérer de l'espace » avant la relance ; sans
          terre, pas de relance (elle échouerait à l'identique) : « Supprimer » clignote. Préparation interrompue par
          un autre terrain passé en priorité : « Reprendre la préparation » clignote. */}
      <Presence show={(failed || cancelled || superseded) && !busy}>
        {(failed || cancelled || superseded) && !busy && (
          <div className="space-y-2">
            {superseded && !failed ? (
              <Callout tone="info" title={t("Préparation interrompue : un autre terrain est passé en priorité.")}>
                {!open
                  ? t("La relance attend la réouverture du terrain libre sur ce serveur.")
                  : t("Les données déjà obtenues sont conservées : la reprise repart de là.")}
              </Callout>
            ) : expected === "storage" ? (
              <StorageCallout message={zone.error_message ? tServer(zone.error_message) : t("La préparation n'a pas abouti.")} />
            ) : (
              <Callout
                tone={failed ? "danger" : "info"}
                title={failed ? (zone.error_message ? tServer(zone.error_message) : t("La préparation n'a pas abouti.")) : t("Préparation annulée.")}
              >
                {expected === "remove"
                  ? t("Supprimez ce terrain, puis tracez un nouvel axe sur la terre ferme.")
                  : !open
                    ? // Terrain libre fermé sur ce serveur : la relance attend sa réouverture (« Réessayer » l'explique).
                      t("La relance attend la réouverture du terrain libre sur ce serveur.")
                    : failed
                    ? // Le message du délai dépassé dit déjà que les données obtenues sont gardées.
                      zone.progress?.error_key === "timeout"
                      ? null
                      : t("Les données déjà obtenues sont conservées : la relance repart de là.")
                    : t("Relancez-la pour utiliser le terrain.")}
              </Callout>
            )}
            {expected !== "remove" && (
              <div className="px-1">
                <Button
                  data-attention={ZONE_TARGETS.retry}
                  variant="secondary"
                  size="sm"
                  highlight={editable && open && wait === 0 && ((expected === "retry" && failed) || superseded)}
                  blocked={!editable || !open || wait > 0}
                  onBlocked={() =>
                    !editable
                      ? designateRights()
                      : open
                      ? notify({ text: t("Préparation de terrain demandée récemment : réessayez dans {duree}.", { duree: durationText(wait) }), tone: "info" })
                      : notifyZonesClosed(notify, false)
                  }
                  onClick={retry}
                  disabled={actions.retry.isPending}
                >
                  <RotateCcw size={14} /> {retryLabel}
                </Button>
              </div>
            )}
          </div>
        )}
      </Presence>

      <Presence show={Boolean(actionError)}>{actionError && <Callout tone="danger">{actionError.message}</Callout>}</Presence>

      {/* Zone prête : surface, date (sources dans l'aide, Crédits et licences). */}
      <Presence show={ready}>
        {ready && (
          <Motion origin="fade">
            {zone.prepared_at && <PropRow label={t("Préparée le")}>{formatDateTime(zone.prepared_at)}</PropRow>}
          </Motion>
        )}
      </Presence>

      {/* Extension tracée, en attente de confirmation. */}
      <Presence show={Boolean(pendingExtension && preview)}>
        {pendingExtension && preview && (
          <Motion origin="top" data-attention="zone-confirm" className="rounded-xl border border-line bg-surface p-3 shadow-card">
            <h4 className="mb-1 text-title font-semibold text-ink">{t("Étendre le terrain")}</h4>
            <PropRow label={t("Surface ajoutée")}>{formatAreaKm2(preview.added_km2)}</PropRow>
            <PropRow label={t("Surface du terrain étendu")}>{formatAreaKm2(preview.area_km2)}</PropRow>
            <p className="mt-1.5 text-meta leading-snug text-ink-muted">{t("Le terrain reste utilisable pendant la préparation de l'extension.")}</p>
            <Presence show={actions.extend.error instanceof ApiError}>
              {actions.extend.error instanceof ApiError && (
                <div className="mt-2">
                  {actions.extend.error.code === STORAGE_FULL ? (
                    <StorageCallout message={actions.extend.error.message} />
                  ) : (
                    <Callout tone="danger">{actions.extend.error.message}</Callout>
                  )}
                </div>
              )}
            </Presence>
            <div className="mt-2.5 flex flex-wrap justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  clearPending();
                  actions.extend.reset();
                }}
              >
                {t("Annuler")}
              </Button>
              <Button
                onClick={() => actions.extend.mutate(pendingExtension.bbox, { onSuccess: () => clearPending() })}
                disabled={actions.extend.isPending}
                blocked={wait > 0}
                onBlocked={() => notify({ text: t("Préparation de terrain demandée récemment : réessayez dans {duree}.", { duree: durationText(wait) }), tone: "info" })}
              >
                <Check size={14} /> {wait > 0 ? t("Préparer dans {duree}", { duree: durationText(wait) }) : t("Préparer l'extension")}
              </Button>
            </div>
          </Motion>
        )}
      </Presence>

      <div className="flex flex-wrap items-center gap-2 py-1">
        {drawingExtension ? (
          <Button variant="secondary" onClick={cancelDrawing}>
            <X size={14} /> {t("Annuler le tracé")}
          </Button>
        ) : (
          <Button
            data-attention={ZONE_TARGETS.extend}
            variant="secondary"
            blocked={!editable || !open || !ready || busy}
            onBlocked={extend}
            onClick={extend}
          >
            <BoxSelect size={14} /> {t("Étendre le terrain")}
          </Button>
        )}
        <Button
          data-attention={ZONE_TARGETS.remove}
          variant="danger-ghost"
          highlight={owned && (expected === "remove" || highlightRemove) && !deleting}
          blocked={!owned}
          onBlocked={designateRights}
          onClick={() => setDeleting(true)}
        >
          <Trash2 size={14} /> {t("Supprimer")}
        </Button>
      </div>
      <Presence show={deleting}>{deleting && <ZoneDeleteDialog zone={zone} onClose={() => setDeleting(false)} />}</Presence>
      <Presence show={Boolean(inUse)}>{inUse && <InUseDialog inUse={inUse} kind="zone" onClose={closeInUse} />}</Presence>
    </div>
  );
}

/** Terrain libre fermé sur ce serveur (relance ou extension demandée sur un terrain existant) : avis explicatif, dans
 *  les mots des Données initiales ; `usable` : terrain prêt (extension), utilisable tel quel, sinon (relance) en
 *  attente de la réouverture. */
function notifyZonesClosed(notify: ReturnType<typeof useUi.getState>["notify"], usable: boolean) {
  notify({
    text: t("Le terrain libre n'est plus ouvert sur ce serveur."),
    detail: usable ? t("Le terrain reste utilisable tel quel.") : t("La relance attend sa réouverture."),
    tone: "info",
  });
}

// --- Zone à recréer ------------------------------------------------------------------------------------------------

interface PendingZone {
  name?: string;
}

/** Zone gardée dans le dossier (import d'un « .generex » ou réception d'un code refusés par la file) : « Recréer la
 *  zone » la remet en file et rattache le dossier. */
export function RecreateZoneCallout({ exercise }: { exercise: Exercise }) {
  const recreate = useRecreateZone();
  const wait = useRetryCountdown("create");
  const notify = useUi((s) => s.notify);
  const open = useZonesOpen();
  const kept = (exercise.parameters.extra?.pending_zone ?? null) as PendingZone | null;
  if (!kept) return null;
  if (!open) {
    // Zone libre fermée sur ce serveur : la description reste gardée, rien n'est proposé qui serait refusé.
    return (
      <div className="px-2 py-1.5">
        <Callout tone="info" title={t("Terrain à recréer : {name}", { name: kept.name ?? t("terrain libre") })}>
          {t("Le terrain libre n'est plus ouvert sur ce serveur : sa description reste gardée dans le dossier, pour le recréer à sa réouverture.")}
        </Callout>
      </div>
    );
  }
  const error = recreate.error instanceof ApiError ? recreate.error : null;
  return (
    <div className="space-y-2 px-2 py-1.5">
      <Callout
        tone="info"
        title={t("Terrain à recréer : {name}", { name: kept.name ?? t("terrain libre") })}
        action={
          <Button
            size="sm"
            data-attention={ZONE_TARGETS.recreate}
            onClick={() => recreate.mutate(exercise)}
            disabled={recreate.isPending}
            blocked={wait > 0}
            onBlocked={() => notify({ text: t("Préparation de terrain demandée récemment : réessayez dans {duree}.", { duree: durationText(wait) }), tone: "info" })}
          >
            {wait > 0 ? t("Recréer dans {duree}", { duree: durationText(wait) }) : t("Recréer le terrain")}
          </Button>
        }
      >
        {t("Le terrain du dossier n'a pas pu être préparé à son arrivée ; sa description est gardée.")}
      </Callout>
      <Presence show={Boolean(error)}>
        {error && (error.code === STORAGE_FULL ? <StorageCallout message={error.message} /> : <Callout tone="danger">{error.message}</Callout>)}
      </Presence>
    </div>
  );
}
